<template>
	<component
		@click="onClick"
		scroll
		:is="tag"
		class="bi-button"
		:class="getClasses"
		:to="to"
		rel="nofollow me noopener noreferrer"
		:href="to"
		target="target"
	>
		<slot></slot>
	</component>
</template>

<script>
export default {
	name: "BiButton",
	data() {
		return {};
	},
	props: {
		tag: {
			type: String,
			default: "a"
		},
		color: {
			type: String,
			validator: value => {
				let colors = [
					"blue",
					"blue-transparent",
					"grey",
					"grey-transparent",
					"transparent",
					"blue-light",
					"primary",
					"gradient-primary",
					"gradient-blue",
					"gradient-green",
					"black",
					"black-transparent",
					"black-bordered",
					"fb",
					"linkeding",
					"youtube",
					"reddit",
					"twitter",
					"google",
					"twitch"
				];
				return colors.indexOf(value) >= 0;
			}
		},
		size: {
			type: String,
			default: "md",
			validator: value => {
				let sizes = ["lg", "md", "sm"];
				return sizes.indexOf(value) >= 0;
			}
		},
		type: {
			type: String,
			default: "rounded",
			validator: value => {
				let types = [
					"squared",
					"rounded-lg",
					"rounded",
					"link",
					"underline-link",
					"icon",
					"squared-icon"
				];
				return types.indexOf(value) >= 0;
			}
		},
		to: [String, Object],
		target: {
			type: String,
			default: null
		}
	},
	computed: {
		getClasses() {
			let classes = [];
			if (this.color) {
				classes.push("bi-" + this.color);
			}
			if (this.type) {
				classes.push("bi-" + this.type);
			}
			if (this.size) {
				classes.push("bi-" + this.size);
			}
			return classes;
		}
	},
	methods: {
		onClick(e) {
			if (scroll) {
				this.scrollTo = "body";
			}
			this.$emit("click", e);
		}
	}
};
</script>

<style scoped lang="scss" type="text/scss">
@import "../../assets/scss/vars";
span.bi-button {
	cursor: default;
}

.bi-button {
	display: inline-block;
	text-align: center;
	text-transform: capitalize;
	cursor: pointer;
	font-family: $inter-ui;
	font-size: 1rem;
	text-decoration: none;
	outline: none;
	transition-property: color, background-color, background-position,
		border-color;
	transition-duration: 0.2s;
	transition-timing-function: ease-in-out;
	color: $white;
	background-color: black;
	border: 2px solid black;

	&:hover {
		border-color: black;
		background-color: black;
	}

	button:focus {
		outline: 0;
	}

	/* Button types */

	&.bi-rounded {
		border-radius: 8em;
		padding: 10px 28px;
	}

	&.bi-rounded-lg {
		border-radius: 8em;
		padding: 10px 70px;
	}

	&.bi-squared {
		border-radius: 0;
		padding: 0.4em 1.25em;
	}

	&.bi-squared-icon {
		border-radius: 0;
		padding: 0.4em 0.51em;
		transition: transform 0.2s ease-out;
		.icon {
			margin-right: 0;
			font-size: 35px;
		}

		&:hover {
			transform: translateY(-15px);
			transition: transform 0.2s ease-out;
		}
	}

	&.bi-link {
		background-color: transparent;
		border-color: transparent;
		color: $cod-gray;
		font-family: $inter-ui;
		font-size: 14px;
		font-weight: 500;
		min-width: auto;
		&:hover {
			background-color: transparent;
		}
	}
	&.bi-underline-link {
		color: $cod-gray;
		padding: 0;
		position: relative;
		background-color: transparent;
		border-color: transparent;
		text-transform: uppercase;
		border-bottom: 2px solid $cod-gray;
		font-weight: 700;
		font-size: 16px;
		height: auto;
		min-width: auto;
		line-height: 26px;
		&:visited {
			color: $cod-gray;
		}

		&:hover {
			background-color: transparent;
			border-bottom-color: $cod-gray;
		}
	}

	&.bi-icon {
		padding: 0px;
		border-radius: 80px;
		height: 37px;
		width: 37px;
		border: 3px solid $cod-gray;
		background-color: transparent;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		i {
			font-size: 30px;
			color: $cod-gray;
			margin: 0;
			display: block;
			transition: color 200ms ease-out;
		}
		&:hover {
			border-color: $gray;
			i {
				color: $gray;
			}
		}
		&.bi-sm {
			border: 1px solid $gray;
			height: 32px;
			width: 32px;
			i {
				color: $gray;
				font-size: 20px;
			}
			&:hover {
				border-color: $cod-gray;
				background: $cod-gray;
				i {
					color: $white;
				}
			}
		}
	}

	/* Button sizes */

	&.bi-sm {
		font-size: 14px;
		letter-spacing: 1.08px;
		font-weight: 400;
		line-height: 14px;
		border: 1px solid;
	}

	&.bi-md {
		font-size: 16px;
		line-height: 23px;
	}

	&.bi-lg {
		font-size: 18px;
		line-height: 24px;
		padding: 10px 64px;
	}

	/* Button colors */

	&.bi-grey-transparent {
		background-color: transparent;
		color: $tundora;
		border-color: $tundora;

		&:hover {
			background-color: black;
			border-color: black;
			color: $white;
		}
	}

	&.bi-transparent {
		background-color: transparent;
		color: $cod-gray;
		border-color: $cod-gray;
		&.bi-sm {
			&.bi-rounded {
				color: $gray;
				border-color: $gray;
				padding: 21px;
				width: 100%;
				max-width: 250px;
				&:hover {
					color: $white;
					border-color: $gray;
					background-color: $gray;
				}
			}
		}
		&.bi-lg {
			&.bi-rounded {
				color: $gray;
				border-color: $gray;
				padding: 10px 60px;
				width: 100%;
				max-width: 250px;
				&:hover {
					color: $white;
					border-color: $gray;
					background-color: $gray;
				}
			}
		}
		&.bi-rounded {
			&:active,
			&:focus {
				background-color: transparent;
				color: $cod-gray;
			}
			&:hover {
				color: $white;
				border-color: black;
				background-color: black;
			}
		}
	}

	&.bi-fb {
		&:active,
		&:focus {
			background-color: transparent;
			color: #3b5998;
		}
		&:hover {
			color: $white;
			border-color: #3b5998;
			background-color: white;
		}
	}

	&.bi-linkedin {
		&:active,
		&:focus {
			background-color: transparent;
			color: #0077b5;
		}
		&:hover {
			color: $white;
			border-color: #0077b5;
			background-color: white;
		}
	}

	&.bi-reddit {
		&:active,
		&:focus {
			background-color: transparent;
			color: #ff4500;
		}
		&:hover {
			color: $white;
			border-color: #ff4500;
			background-color: white;
		}
	}

	&.bi-google {
		&:active,
		&:focus {
			background-color: transparent;
			color: #db4437;
		}
		&:hover {
			color: $white;
			border-color: #db4437;
			background-color: white;
		}
	}

	&.bi-twitch {
		&:active,
		&:focus {
			background-color: transparent;
			color: #6441a5;
		}
		&:hover {
			color: $white;
			border-color: #6441a5;
			background-color: white;
		}
	}

	&.bi-twitter {
		&:active,
		&:focus {
			background-color: transparent;
			color: #1da1f2;
		}
		&:hover {
			color: $white;
			border-color: #1da1f2;
			background-color: white;
		}
	}

	&.bi-youtube {
		&:active,
		&:focus {
			background-color: transparent;
			color: #c4302b;
		}
		&:hover {
			color: $white;
			border-color: #c4302b;
			background-color: white;
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/components/coppiedFooter/Button.vue